import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, ArrowUpDown } from "lucide-react";
import { useDarkMode } from "../hooks/useDarkMode";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtPct, fmtNum } from "./theme";
import { SkeletonCard, NoData, Spark, DeltaBadge, SrTable, BreakBadge, NoteBadge } from "./components";
import PulseLayout from "./PulseLayout";
import { pulseGet } from "./api";

const TAB_INDICATORS = {
  world: ["intl_arrivals", "travel_receipts_usd", "intl_departures", "outbound_expenditure_usd"],
  oecd: ["intl_arrivals", "travel_receipts_ncu", "tourism_gdp_share", "tourism_employment_share"],
};

export default function RankingsPage() {
  const [dm] = useDarkMode();
  const { t, lang, withLang } = usePulseLocale();
  const T = tokens(dm);
  const [tab, setTab] = useState("world");
  const [indicator, setIndicator] = useState("intl_arrivals");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "rank", dir: 1 });

  useEffect(() => {
    let on = true;
    setLoading(true);
    pulseGet("rankings", { indicator, tab, limit: 250 })
      .then((d) => { if (on) setData(d); })
      .catch(() => { if (on) setData(null); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [indicator, tab]);

  const maxYear = useMemo(
    () => Math.max(0, ...(data?.rankings || []).map((r) => parseInt(r.period, 10))),
    [data]);

  const rows = useMemo(() => {
    let out = data?.rankings || [];
    const needle = q.trim().toLowerCase();
    if (needle) out = out.filter((r) => (r.ref_area_label || r.ref_area).toLowerCase().includes(needle) || r.ref_area.toLowerCase().includes(needle));
    const { key, dir } = sort;
    out = [...out].sort((a, b) => {
      const va = a[key] ?? -Infinity, vb = b[key] ?? -Infinity;
      if (key === "ref_area_label") return dir * String(va).localeCompare(String(vb));
      return dir * (va - vb);
    });
    return out;
  }, [data, q, sort]);

  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key ? -s.dir : key === "ref_area_label" || key === "rank" ? 1 : -1 }));

  const exportCsv = () => {
    const head = ["rank", "iso3", "country", "value", "year", "yoy_pct", "cagr_5y", "source"];
    const lines = [head.join(",")].concat(rows.map((r) =>
      [r.rank, r.ref_area, `"${(r.ref_area_label || "").replace(/"/g, '""')}"`, r.value, r.period,
        r.yoy_pct ?? "", r.cagr_5y ?? "", data?.source_org].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `pulse-rankings-${indicator}-${tab}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const switchTab = (next) => {
    setTab(next);
    if (!TAB_INDICATORS[next].includes(indicator)) setIndicator("intl_arrivals");
  };

  const th = (key, label, numeric) => (
    <th scope="col" className={`py-2 font-bold ${numeric ? "text-right" : "text-left"} ${key === "cagr_5y" || key === "period" ? "hidden md:table-cell" : ""}`}>
      <button onClick={() => toggleSort(key)} data-testid={`rankings-sort-${key}`}
        className="inline-flex items-center gap-1 uppercase tracking-wider text-[10px]"
        style={{ color: sort.key === key ? T.accent : T.muted }}>
        {label} <ArrowUpDown className="w-3 h-3" aria-hidden="true" />
      </button>
    </th>
  );

  return (
    <PulseLayout active="rankings">
      <div className="pulse-fade-up">
        <h1 className="pulse-serif text-4xl sm:text-5xl font-medium mb-6" data-testid="rankings-title">{t("rank_title")}</h1>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="flex" role="tablist" aria-label={t("rank_title")}>
            {[["world", t("rank_tab_world")], ["oecd", t("rank_tab_oecd")]].map(([k, lbl]) => (
              <button key={k} role="tab" aria-selected={tab === k} onClick={() => switchTab(k)}
                data-testid={`rankings-tab-${k}`}
                className="text-xs font-bold uppercase tracking-wider px-4 py-2"
                style={{
                  background: tab === k ? T.brand : T.surface,
                  color: tab === k ? "#fff" : T.muted,
                  border: `1px solid ${tab === k ? T.brand : T.border}`,
                }}>
                {lbl}
              </button>
            ))}
          </div>
          <select value={indicator} onChange={(e) => setIndicator(e.target.value)} data-testid="rankings-indicator-select"
            aria-label={t("cmp_indicator")}
            className="px-3 py-2 text-sm outline-none"
            style={{ background: T.surface, border: `1px solid ${T.border}`, color: T.text, borderRadius: 2 }}>
            {TAB_INDICATORS[tab].map((c) => <option key={c} value={c}>{t(`ind_${c}`)}</option>)}
          </select>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("rank_search_ph")}
            aria-label={t("rank_search_ph")} data-testid="rankings-search-input"
            className="px-3 py-2 text-sm outline-none flex-1 min-w-[140px] max-w-xs"
            style={{ background: T.surface, border: `1px solid ${T.border}`, color: T.text, borderRadius: 2 }} />
          <button onClick={exportCsv} disabled={!rows.length} data-testid="rankings-export-csv"
            className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 disabled:opacity-40"
            style={{ color: T.accent, border: `1px solid ${T.border}`, borderRadius: 2 }}>
            <Download className="w-3.5 h-3.5" aria-hidden="true" /> {t("rank_export")}
          </button>
        </div>

        {data && (
          <p className="text-xs mb-3" style={{ color: T.muted }} data-testid="rankings-economies-count">
            {t("rank_economies", { n: fmtNum(data.economies, lang) })} · {t("source")}: {t(`src_${data.source_org}`)}
          </p>
        )}

        {loading && <SkeletonCard h={400} dm={dm} />}
        {!loading && !rows.length && <NoData dm={dm} h={200} testid="rankings-no-data" />}
        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto" style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid="rankings-table">
            <table className="w-full text-sm" style={{ minWidth: 640 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                  <th scope="col" className="px-3 py-2 w-10 text-left text-[10px] uppercase tracking-wider font-bold" style={{ color: T.muted }}>{t("rank_col_rank")}</th>
                  {th("ref_area_label", t("rank_col_country"), false)}
                  {th("value", t("rank_col_value"), true)}
                  {th("period", t("rank_col_year"), true)}
                  {th("yoy_pct", t("rank_col_yoy"), true)}
                  {th("cagr_5y", t("rank_col_cagr"), true)}
                  <th scope="col" className="px-3 py-2 w-24 text-right text-[10px] uppercase tracking-wider font-bold hidden sm:table-cell" style={{ color: T.muted }}>{t("rank_col_trend")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const stale = maxYear && parseInt(r.period, 10) < maxYear - 1;
                  return (
                    <tr key={r.ref_area} className="pulse-row-hover" style={{ borderTop: `1px solid ${T.border}` }}
                      data-testid={`rankings-row-${r.ref_area}`}>
                      <td className="px-3 py-2 text-xs" style={{ color: T.muted }}>{r.rank}</td>
                      <td className="py-2">
                        <Link to={withLang(`/pulse/country/${r.ref_area}`)} className="font-semibold hover:underline" style={{ color: T.text }}>
                          {r.ref_area_label || r.ref_area}
                        </Link>
                        {r.break_affected && <span className="ml-1"><BreakBadge brk={r.series_break} dm={dm} short testid={`rankings-break-${r.ref_area}`} /></span>}
                        {r.area_note && <span className="ml-1"><NoteBadge note={r.area_note} dm={dm} label={t("note_badge_tur")} testid={`rankings-note-${r.ref_area}`} /></span>}
                      </td>
                      <td className="py-2 text-right pulse-num">{fmtCompact(r.value, lang)}</td>
                      <td className="py-2 text-right hidden md:table-cell">
                        <span className="text-xs" style={{ color: stale ? T.expText : T.muted }}>
                          {r.period}
                          {stale && (
                            <span className="ml-1 cursor-help" title={t("stale_note")} data-testid={`stale-flag-${r.ref_area}`}
                              style={{ color: T.expText }}>●</span>
                          )}
                        </span>
                      </td>
                      <td className="py-2 text-right"><DeltaBadge pct={r.yoy_pct} dm={dm} suffix=" " /></td>
                      <td className="py-2 text-right hidden md:table-cell">
                        <span className="text-xs pulse-num" style={{ color: T.muted }}>{fmtPct(r.cagr_5y, lang)}</span>
                      </td>
                      <td className="px-3 py-1 hidden sm:table-cell" style={{ width: 96 }}>
                        <Spark data={r.sparkline} dm={dm} height={26} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <SrTable caption={`${t("rank_title")} — ${t(`ind_${indicator}`)}`}
              columns={[t("rank_col_rank"), t("rank_col_country"), t("rank_col_value"), t("rank_col_year"), t("rank_col_yoy"), t("rank_col_cagr")]}
              rows={rows.map((r) => [r.rank, r.ref_area_label || r.ref_area, fmtCompact(r.value, lang), r.period, fmtPct(r.yoy_pct, lang), fmtPct(r.cagr_5y, lang)])} />
          </div>
        )}
      </div>
    </PulseLayout>
  );
}
